import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import type { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel';

interface UseBookCarouselOptions {
  loop?: boolean;
  dragFree?: boolean;
  align?: 'start' | 'center' | 'end';
  skipSnaps?: boolean;
  containScroll?: 'trimSnaps' | 'keepSnaps' | false;
}

export function useBookCarousel(options: UseBookCarouselOptions = {}) {
  const {
    loop = true,
    dragFree = true,
    align = 'start',
    skipSnaps = false,
    containScroll = 'trimSnaps',
  } = options;

  const emblaOptions: EmblaOptionsType = {
    loop,
    dragFree,
    align,
    skipSnaps,
    containScroll,
    // Improve touch feel with these settings
    dragThreshold: 10,
    duration: 25, // Faster settle for snappier feel
  };

  // WheelGesturesPlugin enables trackpad horizontal scroll and mouse wheel
  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, [WheelGesturesPlugin()]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const onPointerDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('pointerDown', onPointerDown);
    emblaApi.on('pointerUp', onPointerUp);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('pointerDown', onPointerDown);
      emblaApi.off('pointerUp', onPointerUp);
    };
  }, [emblaApi, onSelect, onPointerDown, onPointerUp]);

  return {
    emblaRef,
    emblaApi,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
    isDragging,
  };
}
